import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route } from "react-router-dom";
import { Card } from "antd";
import {
  UserOutlined,
  LockOutlined,
  ShoppingOutlined,
  AppstoreAddOutlined,
  UnorderedListOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  HomeOutlined,
} from "@ant-design/icons";

import ContentTitle from "../../components/ContentTitle/ContentTitle";
import AccountLink from "./AccountLink/AccountLink";
import PersonalData from "./PersonalData/PersonalData";
import AddPerfume from "./AddPerfume/AddPerfume";
import PerfumeList from "./PerfumeList/PerfumeList";
import EditPerfume from "./EditPerfume/EditPerfume";
import ManageHome from "./ManageHome/ManageHome";
import OrdersList from "./OrdersList/OrdersList";
import ManageUserOrder from "./ManageUserOrder/ManageUserOrder";
import UsersList from "./UsersList/UsersList";
import ManageUser from "./ManageUser/ManageUser";
import ChangePassword from "./ChangePassword/ChangePassword";
import PersonalOrdersList from "./PersonalOrdersList/PersonalOrdersList";
import AddPincode from "./AddPincode/AddPincode";

import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { resetAuthState } from "../../redux-toolkit/auth/auth-slice";
import { fetchUserInfo } from "../../redux-toolkit/user/user-thunks";
import { UserRoles } from "../../types/types";
import {
  ACCOUNT,
  ACCOUNT_ADMIN_ADD,
  ACCOUNT_ADMIN_ORDERS,
  ACCOUNT_ADMIN_HOME,
  ACCOUNT_ADMIN_PERFUMES,
  ACCOUNT_ADMIN_USERS,
  ACCOUNT_ADMIN_EDIT,
  ACCOUNT_USER_EDIT,
  ACCOUNT_USER_INFO,
  ACCOUNT_USER_ORDERS,
  ACCOUNT_ADMIN_PINCODES,
} from "../../constants/routeConstants";

import "./Account.css";

const Account: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const usersData = useSelector(selectUserFromUserState);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    dispatch(resetAuthState());
    dispatch(fetchUserInfo());
  }, [dispatch]);

  useEffect(() => {
    if (usersData) {
      setIsAdmin(usersData.roles?.[0] === UserRoles.ADMIN);
    }
  }, [usersData]);

  const SidebarLinks = (
    <div className="account-links">
      {isAdmin ? (
        <>
          <AccountLink link={ACCOUNT_USER_INFO} title="Personal Data" icon={<UserOutlined />} />
          <AccountLink link={ACCOUNT_ADMIN_ADD} title="Add Product" icon={<AppstoreAddOutlined />} />
          <AccountLink link={ACCOUNT_ADMIN_PERFUMES} title="List of Products" icon={<UnorderedListOutlined />} />
          <AccountLink link={ACCOUNT_ADMIN_ORDERS} title="All Orders" icon={<ShoppingOutlined />} />
          <AccountLink link={ACCOUNT_ADMIN_USERS} title="All Users" icon={<TeamOutlined />} />
          <AccountLink link={ACCOUNT_ADMIN_HOME} title="Manage Home Page" icon={<HomeOutlined />} />
          <AccountLink
            link={ACCOUNT_ADMIN_PINCODES}
            title="Manage Pincodes"
            icon={<EnvironmentOutlined />}
          />
        </>
      ) : (
        <>
          <AccountLink link={ACCOUNT_USER_INFO} title="Personal Data" icon={<UserOutlined />} />
          <AccountLink link={ACCOUNT_USER_EDIT} title="Change Password" icon={<LockOutlined />} />
          <AccountLink link={ACCOUNT_USER_ORDERS} title="My Orders" icon={<ShoppingOutlined />} />
        </>
      )}
    </div>
  );

  return (
    <div className="account-container">
      {/* Mobile Header */}
      <div className="account-mobile-header">
        <h3>My Account</h3>
      </div>

      <div className="account-layout">
        {/* Desktop Sidebar */}
        <div className="account-sidebar">
          <Card className="sidebar-card">
            <ContentTitle title="My Account" titleLevel={4} icon={<UserOutlined />} />
            {SidebarLinks}
          </Card>
        </div>

        {/* Main Content */}
        <div className="account-content">
          <div className="mobile-links">{SidebarLinks}</div>

          {/* ✅ Skip outer Card for ManageUserOrder */}
          <Route
            exact
            path={`${ACCOUNT_USER_ORDERS}/:id`}
            render={() => <ManageUserOrder />}
          />

          <Route
            exact
            path={ACCOUNT_ADMIN_PINCODES}
            component={AddPincode}
          />

          {/* ✅ Wrap others inside Card */}
          <Route
            exact
            path={[
              ACCOUNT_USER_INFO,
              ACCOUNT_USER_EDIT,
              ACCOUNT_USER_ORDERS,
              ACCOUNT_ADMIN_ADD,
              ACCOUNT_ADMIN_PERFUMES,
              ACCOUNT_ADMIN_ORDERS,
              ACCOUNT_ADMIN_USERS,
              ACCOUNT_ADMIN_HOME,
            ]}
            render={() => (
              <Card className="content-card">
                <Route path={ACCOUNT_USER_INFO} component={PersonalData} />
                <Route path={ACCOUNT_USER_EDIT} component={ChangePassword} />
                <Route
                  exact
                  path={ACCOUNT_USER_ORDERS}
                  component={PersonalOrdersList}
                />

                {isAdmin && (
                  <>
                    <Route path={ACCOUNT_ADMIN_ADD} component={AddPerfume} />
                    <Route exact path={ACCOUNT_ADMIN_PERFUMES} component={PerfumeList} />
                    <Route exact path={`${ACCOUNT_ADMIN_PERFUMES}/:id`} component={EditPerfume} />
                    <Route exact path={ACCOUNT_ADMIN_ORDERS} component={OrdersList} />
                    <Route exact path={ACCOUNT_ADMIN_USERS} component={UsersList} />
                    <Route exact path={`${ACCOUNT_ADMIN_USERS}/:id`} component={ManageUser} />
                    <Route exact path={ACCOUNT_ADMIN_HOME} component={ManageHome} />
                  </>
                )}
              </Card>
            )}
          />

          {/* ✅ Default Redirect */}
          <Route exact path={ACCOUNT}>
            {isAdmin ? (
              <Redirect to={ACCOUNT_ADMIN_PERFUMES} />
            ) : (
              <Redirect to={ACCOUNT_USER_INFO} />
            )}
          </Route>
        </div>
      </div>
    </div>
  );
};

export default Account;
